import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { FiCalendar } from 'react-icons/fi';
import api from '../config/api';
import LoadingButton from './LoadingButton';
import CrmModalShell from './CrmModalShell';

function toIsoDate(d) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatSlotLabel(iso) {
  try {
    return new Date(iso).toLocaleString([], {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/**
 * Candidate (or consultant self-test): pick assigned consultant + open slot.
 */
export default function BookAppointmentModal({ open, onClose, initialSlot = null }) {
  const queryClient = useQueryClient();
  const [consultantSlug, setConsultantSlug] = useState('');
  const [slot, setSlot] = useState(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [dayFrom, setDayFrom] = useState(() => toIsoDate(new Date()));

  const dayTo = useMemo(() => {
    const d = new Date(`${dayFrom}T00:00:00`);
    d.setDate(d.getDate() + 7);
    return toIsoDate(d);
  }, [dayFrom]);

  const { data: assigned = [], isLoading: loadingAssigned } = useQuery(
    ['booking-pages-assigned'],
    () => api.get('/booking-pages/assigned').then((r) => r.data),
    { enabled: open, staleTime: 60_000 }
  );

  const consultants = useMemo(() => {
    const list = Array.isArray(assigned) ? assigned : assigned?.items || [];
    return list.filter((c) => c.slug && c.is_active !== 0 && c.is_active !== false);
  }, [assigned]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setNotes('');
    setDayFrom(toIsoDate(initialSlot?.starts_at ? new Date(initialSlot.starts_at) : new Date()));
    setSlot(null);
  }, [open, initialSlot]);

  useEffect(() => {
    if (!open) return;
    if (!consultantSlug && consultants[0]?.slug) {
      setConsultantSlug(consultants[0].slug);
    }
  }, [open, consultants, consultantSlug]);

  const { data: slotsData, isLoading: loadingSlots } = useQuery(
    ['booking-slots', consultantSlug, dayFrom, dayTo],
    () =>
      api
        .get(`/public/booking/${encodeURIComponent(consultantSlug)}/slots`, {
          params: { from: dayFrom, to: dayTo },
        })
        .then((r) => r.data),
    { enabled: open && Boolean(consultantSlug), staleTime: 15_000 }
  );

  const slots = useMemo(() => {
    const list = slotsData?.slots || slotsData || [];
    return Array.isArray(list) ? list : [];
  }, [slotsData]);

  useEffect(() => {
    setSlot(null);
  }, [consultantSlug, dayFrom]);

  const bookMutation = useMutation(
    () =>
      api.post(`/booking-pages/${encodeURIComponent(consultantSlug)}/book`, {
        starts_at: slot.starts_at,
        ends_at: slot.ends_at,
        notes: notes || undefined,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['crm-calendar']);
        queryClient.invalidateQueries(['crm']);
        onClose?.();
      },
      onError: (e) => setError(e.response?.data?.error || e.message),
    }
  );

  return (
    <CrmModalShell
      open={open}
      onClose={onClose}
      icon={FiCalendar}
      title="Book appointment"
      subtitle="Choose a published open slot from your consultant. You’ll get a Google Meet link."
      ariaLabel="Book appointment"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <LoadingButton
            type="button"
            className="btn btn-primary"
            disabled={!slot || !consultantSlug}
            loading={bookMutation.isLoading}
            onClick={() => {
              setError('');
              bookMutation.mutate();
            }}
          >
            Confirm booking
          </LoadingButton>
        </>
      }
    >
      {error && (
        <p className="crm-modal-alert" role="alert">
          {error}
        </p>
      )}
      {loadingAssigned ? (
        <p className="crm-modal-hint">Loading consultants…</p>
      ) : consultants.length === 0 ? (
        <div className="crm-modal-empty">
          No active booking calendars from your assigned consultants yet.
        </div>
      ) : (
        <>
          <label className="form-field">
            <span>Consultant</span>
            <select value={consultantSlug} onChange={(e) => setConsultantSlug(e.target.value)}>
              {consultants.map((c) => (
                <option key={c.slug} value={c.slug}>
                  {c.title ||
                    [c.first_name, c.last_name].filter(Boolean).join(' ') ||
                    c.slug}
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span>Week starting</span>
            <input type="date" value={dayFrom} onChange={(e) => setDayFrom(e.target.value)} />
          </label>
          <div className="form-field">
            <span>Available slots</span>
            {loadingSlots ? (
              <p className="crm-modal-hint">Loading slots…</p>
            ) : slots.length === 0 ? (
              <div className="crm-modal-empty">
                No open slots yet. Your consultant must publish weekly booking hours before you can
                reserve a time.
              </div>
            ) : (
              <div className="crm-slot-grid" role="listbox" aria-label="Available slots">
                {slots.map((s) => {
                  const key = s.starts_at;
                  const active = slot?.starts_at === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      role="option"
                      aria-selected={active}
                      className={`crm-slot-chip ${active ? 'is-active' : ''}`}
                      onClick={() => setSlot(s)}
                    >
                      {formatSlotLabel(s.starts_at)}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <label className="form-field">
            <span>Notes (optional)</span>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything your consultant should know"
            />
          </label>
        </>
      )}
    </CrmModalShell>
  );
}
